import * as Api from '@ViewModels';
import React from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { createRichContentEditorStateWithText } from '../../../../../models/UiUtils';
import { CustomSurveyFormData } from '../utils';

interface UseCustomSurveyFormInitializationProps {
	surveyData?: Api.ICustomSurvey;
	isClone: boolean;
	setValue: UseFormSetValue<CustomSurveyFormData>;
}

// Helper function to calculate updateExistingContacts state from field values
const calculateUpdateExistingContacts = (surveyData?: Api.ICustomSurvey): boolean => {
	if (!surveyData) return false; // Default for new surveys

	// If any contact field has updateExisting true, return true
	return (
		surveyData.options?.contactNameOptions?.updateExisting ||
		surveyData.options?.contactEmailOptions?.updateExisting ||
		surveyData.options?.contactPhoneNumberOptions?.updateExisting ||
		surveyData.options?.contactBirthdayOptions?.updateExisting ||
		surveyData.options?.contactAddressOptions?.updateExisting ||
		false
	);
};

// Hook for form initialization and cloning logic
export const useCustomSurveyFormInitialization = ({
	surveyData,
	isClone,
	setValue,
}: UseCustomSurveyFormInitializationProps) => {
	React.useEffect(() => {
		if (surveyData) {
			setValue('name', isClone ? `${surveyData.name} Copy` : surveyData.name);
			setValue('customForm.name', isClone ? `${surveyData.name} Copy` : surveyData.name);
			setValue('startDate', surveyData?.startDate);

			// Set hasDateRange if it exists
			if (surveyData.hasDateRange !== undefined) {
				setValue('hasDateRange', surveyData.hasDateRange);
			}

			// Calculate updateExistingContacts state from actual field values
			const updateExistingContacts = calculateUpdateExistingContacts(surveyData);
			setValue('updateExistingContacts', updateExistingContacts);

			setValue('options', {
				contactNameOptions: surveyData.options?.contactNameOptions,
				contactEmailOptions: surveyData.options?.contactEmailOptions,
				contactPhoneNumberOptions: surveyData.options?.contactPhoneNumberOptions,
				contactBirthdayOptions: surveyData.options?.contactBirthdayOptions,
				contactAddressOptions: surveyData.options?.contactAddressOptions,
				intro: createRichContentEditorStateWithText(surveyData.options?.intro?.document),
				sendConfirmationMessage: surveyData.options?.sendConfirmationMessage,
				includeCompanyLogo: surveyData.options?.includeCompanyLogo,
				companyName: surveyData.options?.companyName || '',
				confirmationMessage: createRichContentEditorStateWithText(surveyData.options?.confirmationMessage?.document),
			});

			if (surveyData?.notificationSettings) {
				setValue('notificationSettings', {
					_type: 'SurveyNotificationSettings',
					isEnabled: surveyData.notificationSettings.isEnabled,
					additionalRecipients: surveyData.notificationSettings.additionalRecipients,
				});
			}

			if (surveyData.expirationDate) {
				setValue('expirationDate', surveyData.expirationDate);
			}

			if (surveyData.customForm?.fields) {
				if (isClone) {
					// Clone fields with new IDs
					const fieldIdMap = new Map<string, string>();
					const cloneFields = surveyData.customForm.fields.map(field => {
						const newFieldId = uuid();
						fieldIdMap.set(field.id, newFieldId);
						return {
							...field,
							id: newFieldId,
							name: newFieldId, // Generate new name field for response key isolation
							isHidden: field.isHidden,
							isOptional: field.isOptional,
							label: field.label,
							fieldType: field.fieldType,
							options:
								field.options?.map(option => ({
									...option,
									id: uuid(), // Generate new option ID for clone
									fieldId: newFieldId, // Update option fieldId to reference the new field ID
								})) || [],
						};
					});
					setValue('customForm.fields', cloneFields);

					// Update tagGenerationSettings with new field IDs for clones
					if (surveyData.tagGenerationSettings) {
						setValue('tagGenerationSettings', {
							tagOnCompletion: surveyData.tagGenerationSettings.tagOnCompletion || false,
							completionTag: surveyData.tagGenerationSettings.completionTag || '',
							customFormFieldTagSettings:
								surveyData.tagGenerationSettings.customFormFieldTagSettings?.map(tagSetting => ({
									...tagSetting,
									fieldId: fieldIdMap.get(tagSetting.fieldId) || tagSetting.fieldId,
								})) || [],
							effectiveCompletionTag: surveyData.tagGenerationSettings.effectiveCompletionTag || undefined,
						});
					}

					// Update customFieldMap with new field IDs for clones
					if (surveyData.customFieldMap) {
						setValue(
							'customFieldMap',
							surveyData.customFieldMap.map(mapping => ({
								...mapping,
								fieldId: fieldIdMap.get(mapping.fieldId) || mapping.fieldId,
							}))
						);
					}
				} else {
					// When not cloning, use original data as-is but ensure type safety
					setValue(
						'customForm.fields',
						surveyData.customForm.fields.map(field => ({
							...field,
							id: field.id || uuid(), // Ensure ID exists
							label: field.label || '',
							isHidden: field.isHidden || false,
							isOptional: field.isOptional || false,
							options: field.options || [],
						}))
					);

					// Initialize tagGenerationSettings without field ID mapping
					if (surveyData.tagGenerationSettings) {
						setValue('tagGenerationSettings', {
							tagOnCompletion: surveyData.tagGenerationSettings.tagOnCompletion || false,
							completionTag: surveyData.tagGenerationSettings.completionTag || '',
							customFormFieldTagSettings: surveyData.tagGenerationSettings.customFormFieldTagSettings || [],
							effectiveCompletionTag: surveyData.tagGenerationSettings.effectiveCompletionTag || undefined,
						});
					}

					// Initialize customFieldMap without field ID mapping
					if (surveyData.customFieldMap) {
						setValue('customFieldMap', surveyData.customFieldMap);
					}
				}
			} else {
				setValue('customForm.fields', [
					{
						fieldType: Api.FormFieldType.String,
						id: uuid(),
						label: '',
						isHidden: false,
						isOptional: true,
						options: [],
					},
				]);

				// Initialize tagGenerationSettings even when no fields exist
				if (surveyData.tagGenerationSettings) {
					setValue('tagGenerationSettings', {
						tagOnCompletion: surveyData.tagGenerationSettings.tagOnCompletion || false,
						completionTag: surveyData.tagGenerationSettings.completionTag || '',
						customFormFieldTagSettings: surveyData.tagGenerationSettings.customFormFieldTagSettings || [],
						effectiveCompletionTag: surveyData.tagGenerationSettings.effectiveCompletionTag || undefined,
					});
				}
			}
		}
	}, [surveyData, setValue, isClone]);
};
